import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaDownload, FaPaperPlane } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import HeaderCard from '../../components/Header/HeaderCard';
import TableList from '../../components/Tools/TableList';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { notify } from '../../components/Utils/Notifications';

/**
 * Émission et liste des ordres de mission de la société (Yros/Kavero#127),
 * avec le téléchargement des documents de chaque ordre (Yros/Kavero#128) :
 * l'ordre de mission et l'engagement de retour (#425), chacun en PDF ou en
 * Word (#465), depuis un menu « Télécharger » par ligne. Le menu s'ouvre dans
 * la cellule plutôt qu'en surimpression : la colonne d'actions est épinglée
 * dans un tableau qui défile, un panneau flottant y serait rogné. Aucun menu
 * sur un ordre annulé, dont Odoo refuse tout document. Cet écran est réservé
 * au gestionnaire RH (`canEmettreOrdresMission`), seul à obtenir le Word.
 *
 * Liste : patron de `SupervisionRh.js`, un seul appel et une recherche locale
 * sur le nom du salarié. Formulaire : patron de `pages/Espace/DemandeConge.js`,
 * le salarié choisi avec `EmployeeSelect`, la source que le portail utilise
 * déjà. Le portail ne juge que la présence des champs : salarié hors société,
 * dates ou chevauchement, c'est Odoo qui refuse, et son message est notifié
 * mot pour mot.
 *
 * Statut (Yros/Kavero#441, contrat C21) : chaque ligne montre son statut et ne
 * propose que les transitions que la machine à états de Yros/Kavero#423
 * permet. Odoo reste juge (409, message notifié mot pour mot) ; la ligne ne
 * change qu'après sa réponse, sans recharger la liste.
 */

// Documents d'un ordre et leur message d'échec ; formats proposés pour chacun.
const DOCUMENTS = [
  { code: 'ordre', libelle: 'ordresMission.documentOrdre', erreur: 'ordresMission.erreurTelechargement' },
  {
    code: 'engagement',
    libelle: 'ordresMission.documentEngagement',
    erreur: 'ordresMission.erreurTelechargementEngagement',
  },
];
const FORMATS = [
  { code: 'pdf', libelle: 'ordresMission.formatPdf' },
  { code: 'docx', libelle: 'ordresMission.formatWord' },
];

// Codes du contrat Yros/Kavero#125 ; seuls leurs libellés se traduisent.
const MOYENS_TRANSPORT = ['avion', 'vehicule_service', 'vehicule_personnel', 'transport_commun', 'autre'];

// Machine à états de Yros/Kavero#423, recopiée pour n'offrir que les actions
// permises. Remis et annulé sont terminaux.
const STATUTS = ['en_attente', 'disponible', 'remis', 'annule'];
const TRANSITIONS = {
  en_attente: ['disponible', 'annule'],
  disponible: ['remis', 'annule'],
  remis: [],
  annule: [],
};

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

const FORMULAIRE_VIDE = {
  salarie: null,
  destination: '',
  objet: '',
  dateDepart: '',
  dateRetour: '',
  moyenTransport: '',
};

function OrdresMission() {
  const { t } = useTranslation();

  const [ordres, setOrdres] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);
  const [envoi, setEnvoi] = useState(false);
  // Téléchargements en cours, par clé `<id>-<document>-<format>` : chaque
  // entrée du menu a son propre état d'attente.
  const [telechargements, setTelechargements] = useState(() => new Set());
  const [menuOuvertId, setMenuOuvertId] = useState(null);
  const [transitionId, setTransitionId] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await apiService.listerOrdresMission('societe');
      if (reponse?.ok === false) {
        setOrdres([]);
        setErreur(reponse.message || t('ordresMission.erreurGenerique'));
      } else {
        setOrdres(reponse?.ordres || []);
      }
    } catch (e) {
      signalerErreur(e, 'OrdresMission.charger');
      setOrdres([]);
      setErreur(t('ordresMission.erreurGenerique'));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    if (menuOuvertId === null) return undefined;
    const fermerSurEchap = (event) => {
      if (event.key === 'Escape') setMenuOuvertId(null);
    };
    document.addEventListener('keydown', fermerSurEchap);
    return () => document.removeEventListener('keydown', fermerSurEchap);
  }, [menuOuvertId]);

  const changer = (champ, valeur) => setFormulaire((courant) => ({ ...courant, [champ]: valeur }));

  const emettre = async (event) => {
    event.preventDefault();
    const { salarie, destination, objet, dateDepart, dateRetour, moyenTransport } = formulaire;
    if (!salarie || !destination.trim() || !objet.trim() || !dateDepart || !dateRetour) {
      notify(t('ordresMission.champsManquants'), '', 4);
      return;
    }

    setEnvoi(true);
    try {
      const reponse = await apiService.emettreOrdreMission({
        employe_id: salarie.value,
        destination,
        objet,
        date_depart: dateDepart,
        date_retour: dateRetour,
        moyen_transport: moyenTransport || null,
      });
      if (reponse?.ok) {
        notify(t('ordresMission.succes', { numero: reponse.ordre?.numero }), '', 2);
        setFormulaire(FORMULAIRE_VIDE);
        charger();
      } else {
        notify(reponse?.message || t('ordresMission.erreurGenerique'), '', 3);
      }
    } catch (e) {
      // Seul le message métier d'un refus (409) s'affiche : le corps d'une
      // panne (502) porte un `error` technique, pas une phrase pour la RH.
      notify(e?.response?.data?.message || t('ordresMission.erreurGenerique'), '', 3);
    } finally {
      setEnvoi(false);
    }
  };

  const telecharger = async (ordre, document, format) => {
    const cle = `${ordre.id}-${document.code}-${format}`;
    setTelechargements((courants) => new Set(courants).add(cle));
    try {
      await apiService.telechargerDocumentOrdreMission(ordre, document.code, format);
    } catch (e) {
      // Le corps d'erreur arrive en blob, illisible : le message reste local.
      signalerErreur(e, 'OrdresMission.telecharger');
      notify(t(document.erreur), '', 3);
    } finally {
      setTelechargements((courants) => {
        const suivants = new Set(courants);
        suivants.delete(cle);
        return suivants;
      });
    }
  };

  const changerStatut = async (ordre, statut) => {
    setTransitionId(ordre.id);
    try {
      const reponse = await apiService.changerStatutOrdreMission(ordre.id, statut);
      if (reponse?.ok && reponse.ordre) {
        setOrdres((courants) => courants.map((o) => (o.id === ordre.id ? reponse.ordre : o)));
      } else {
        notify(reponse?.message || t('ordresMission.erreurStatut'), '', 3);
      }
    } catch (e) {
      // Comme l'émission : seul le message métier d'un refus (409) s'affiche.
      notify(e?.response?.data?.message || t('ordresMission.erreurStatut'), '', 3);
    } finally {
      setTransitionId(null);
    }
  };

  const ordresAffiches = useMemo(() => {
    // `setSearchQuery` de TableList rend un tableau de termes : seul le premier
    // compte, comme dans SupervisionRh.js.
    const brut = Array.isArray(recherche) ? recherche[0] : recherche;
    const terme = (brut || '').trim().toLowerCase();
    return ordres.filter((ordre) => !terme || (ordre.employe?.nom || '').toLowerCase().includes(terme));
  }, [ordres, recherche]);

  const colonnes = useMemo(
    () => [
      {
        key: 'numero',
        label: t('ordresMission.colNumero'),
        render: (ordre) => <span data-testid={`ordre-ligne-${ordre.id}`}>{ordre.numero}</span>,
      },
      { key: 'salarie', label: t('ordresMission.colSalarie'), render: (ordre) => ordre.employe?.nom || '—' },
      { key: 'destination', label: t('ordresMission.colDestination'), render: (ordre) => ordre.destination },
      {
        key: 'periode',
        label: t('ordresMission.colPeriode'),
        render: (ordre) => `${ordre.date_depart} → ${ordre.date_retour}`,
      },
      {
        key: 'transport',
        label: t('ordresMission.colTransport'),
        render: (ordre) =>
          ordre.moyen_transport
            ? t(`ordresMission.transports.${ordre.moyen_transport}`, { defaultValue: ordre.moyen_transport })
            : '—',
      },
      { key: 'emisPar', label: t('ordresMission.colEmisPar'), render: (ordre) => ordre.emis_par?.nom || '—' },
      { key: 'dateEmission', label: t('ordresMission.colDateEmission'), render: (ordre) => ordre.date_emission },
      {
        key: 'statut',
        label: t('ordresMission.colStatut'),
        render: (ordre) => (
          <span data-testid={`ordre-statut-${ordre.id}`}>
            {STATUTS.includes(ordre.statut) ? t(`ordresMission.statut.${ordre.statut}`) : '—'}
          </span>
        ),
      },
    ],
    [t]
  );

  return (
    <div className="p-4 space-y-4">
      <HeaderCard title={t('ordresMission.titre')} text={t('ordresMission.description')} />

      <form
        data-testid="ordre-formulaire"
        onSubmit={emettre}
        className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-100 space-y-4"
      >
        <h2 className="text-base sm:text-lg font-semibold">{t('ordresMission.nouvelOrdre')}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-sm font-medium">
            <span>{t('ordresMission.salarie')}</span>
            <div className="mt-1">
              <EmployeeSelect value={formulaire.salarie} onChange={(option) => changer('salarie', option)} />
            </div>
          </div>

          <label className="block text-sm font-medium">
            {t('ordresMission.destination')}
            <input
              type="text"
              data-testid="ordre-destination"
              value={formulaire.destination}
              onChange={(e) => changer('destination', e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>

          <label className="block text-sm font-medium md:col-span-2">
            {t('ordresMission.objet')}
            <input
              type="text"
              data-testid="ordre-objet"
              value={formulaire.objet}
              onChange={(e) => changer('objet', e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>

          <label className="block text-sm font-medium">
            {t('ordresMission.dateDepart')}
            <input
              type="date"
              data-testid="ordre-date-depart"
              value={formulaire.dateDepart}
              onChange={(e) => changer('dateDepart', e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>

          <label className="block text-sm font-medium">
            {t('ordresMission.dateRetour')}
            <input
              type="date"
              data-testid="ordre-date-retour"
              min={formulaire.dateDepart || undefined}
              value={formulaire.dateRetour}
              onChange={(e) => changer('dateRetour', e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>

          <label className="block text-sm font-medium">
            {t('ordresMission.moyenTransport')}
            <select
              data-testid="ordre-moyen-transport"
              value={formulaire.moyenTransport}
              onChange={(e) => changer('moyenTransport', e.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">{t('ordresMission.aucunTransport')}</option>
              {MOYENS_TRANSPORT.map((code) => (
                <option key={code} value={code}>
                  {t(`ordresMission.transports.${code}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            data-testid="ordre-emettre"
            disabled={envoi}
            className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FaPaperPlane size={14} />
            {envoi ? t('ordresMission.envoi') : t('ordresMission.emettre')}
          </button>
        </div>
      </form>

      {erreur && (
        <p
          data-testid="ordres-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
        >
          {erreur}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : (
        !erreur && (
          <TableList
            columns={colonnes}
            data={ordresAffiches}
            searchQuery={recherche}
            setSearchQuery={setRecherche}
            showFilter={false}
            // Les boutons de la dernière colonne (transitions + menu des
            // documents) sortent du cadre sur un écran de portable : on épingle
            // la colonne à droite.
            stickyActions
            extraActions={(ordre) => (
              <div className="inline-flex items-center gap-3 flex-wrap">
                {(TRANSITIONS[ordre.statut] || []).map((cible) => (
                  <button
                    key={cible}
                    type="button"
                    data-testid={`ordre-transition-${ordre.id}-${cible}`}
                    onClick={() => changerStatut(ordre, cible)}
                    disabled={transitionId === ordre.id}
                    className={`text-sm font-medium hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed ${
                      cible === 'annule' ? 'text-red-600' : 'text-[--color-primary]'
                    }`}
                  >
                    {t(`ordresMission.transitions.${cible}`)}
                  </button>
                ))}
                {ordre.statut !== 'annule' && (
                  <div className="inline-flex flex-col items-start">
                    <button
                      type="button"
                      data-testid={`ordre-documents-${ordre.id}`}
                      onClick={() => setMenuOuvertId((courant) => (courant === ordre.id ? null : ordre.id))}
                      aria-expanded={menuOuvertId === ordre.id}
                      aria-controls={`ordre-documents-menu-${ordre.id}`}
                      aria-label={t('ordresMission.documentsAria', { numero: ordre.numero })}
                      className="inline-flex items-center gap-2 text-[--color-primary] hover:opacity-80 text-sm font-medium"
                    >
                      <FaDownload className="w-4 h-4 shrink-0" aria-hidden="true" />
                      {t('ordresMission.documents')}
                      <FaChevronDown className="w-3 h-3 shrink-0" aria-hidden="true" />
                    </button>
                    {menuOuvertId === ordre.id && (
                      <div
                        id={`ordre-documents-menu-${ordre.id}`}
                        className="mt-2 space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm"
                      >
                        {DOCUMENTS.map((document) => (
                          <div key={document.code} className="flex items-center justify-between gap-4 whitespace-nowrap">
                            <span className="text-gray-700">{t(document.libelle)}</span>
                            <span className="inline-flex items-center gap-2">
                              {FORMATS.map((format) => (
                                <button
                                  key={format.code}
                                  type="button"
                                  data-testid={`ordre-telecharger-${ordre.id}-${document.code}-${format.code}`}
                                  onClick={() => telecharger(ordre, document, format.code)}
                                  // Garde contre le double clic : React ne
                                  // dispatche aucun clic sur un bouton désactivé.
                                  disabled={telechargements.has(`${ordre.id}-${document.code}-${format.code}`)}
                                  aria-label={`${t(document.libelle)} (${t(format.libelle)})`}
                                  className="font-medium text-[--color-primary] hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {t(format.libelle)}
                                </button>
                              ))}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          />
        )
      )}
    </div>
  );
}

export default OrdresMission;
